import { api, type CursorPage } from '@/api/client';
import type {
  Account,
  ConsumeSummary,
  Contract,
  Invoice,
  LedgerFlow,
  PaymentChannels,
  RechargeOrder,
  SeatSubscription,
} from '@/api/types.contract';

export const getAccount = (signal?: AbortSignal) =>
  api.get<Account>('/account', signal ? { signal } : {});

export interface FlowListParams {
  kind?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  cursor?: string | undefined;
  limit?: number | undefined;
}

export const listFlows = (p: FlowListParams, signal?: AbortSignal) =>
  api.get<CursorPage<LedgerFlow>>('/account/flows', {
    query: { ...p },
    ...(signal ? { signal } : {}),
  });

export const createRecharge = (body: {
  amount_micro: number;
  pay_method: string;
  channel?: string | undefined;
}) => api.post<RechargeOrder>('/recharge-orders', { body });

export const getRechargeStatus = (id: string, signal?: AbortSignal) =>
  api.get<RechargeOrder>(`/recharge-orders/${id}/status`, signal ? { signal } : {});

export const listPaymentChannels = (signal?: AbortSignal) =>
  api.get<PaymentChannels>('/payment/channels', signal ? { signal } : {});

/**
 * 退订 = 关自动续费：当期已付费用不退、产能保留到期末（spec 05 §5）。
 * 没有「购买坐席」：坐席随雇佣自动开通并按月续费。
 */
export const unsubscribeSeats = (body: { subscription_id: string }) =>
  api.post<void>('/seats/unsubscribe', { body });

export const listSeatSubscriptions = (signal?: AbortSignal) =>
  api.get<{ items: SeatSubscription[] }>('/seats', signal ? { signal } : {});

export const getConsumeSummary = (p: { from: string; to: string }, signal?: AbortSignal) =>
  api.get<ConsumeSummary>('/account/consume', {
    query: { ...p },
    ...(signal ? { signal } : {}),
  });

/** 对公转账凭证上传。走 multipart，见 leads.ts 里同样的取舍说明。 */
export const uploadRechargeCredential = (
  id: string,
  body: { file_url: string; note: string | null },
) => api.patch<RechargeOrder>(`/recharge-orders/${id}/credentials`, { body });

export const listContracts = (
  p: { cursor?: string | undefined; limit?: number | undefined },
  signal?: AbortSignal,
) =>
  api.get<CursorPage<Contract>>('/contracts', {
    query: { ...p },
    ...(signal ? { signal } : {}),
  });

export const listInvoices = (
  p: { cursor?: string | undefined; limit?: number | undefined },
  signal?: AbortSignal,
) =>
  api.get<CursorPage<Invoice>>('/invoices', { query: { ...p }, ...(signal ? { signal } : {}) });

export const applyInvoice = (body: { amount_micro: number; type: string }) =>
  api.post<Invoice>('/invoices', { body });

export const invoiceFileUrl = (id: string) => `/invoices/${id}/file`;

const API_BASE = import.meta.env['VITE_API_BASE'] ?? '/console/v1';

export async function downloadInvoiceFile(id: string, accessToken: string | null): Promise<void> {
  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  const res = await fetch(`${API_BASE}${invoiceFileUrl(id)}`, { headers });
  if (!res.ok) {
    let msg = `下载失败（HTTP ${res.status}）`;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body.error?.message) msg = body.error.message;
    } catch {
      /* keep */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const star = /filename\*=UTF-8''([^;]+)/i.exec(res.headers.get('Content-Disposition') ?? '');
  const plain = /filename="?([^";]+)"?/i.exec(res.headers.get('Content-Disposition') ?? '');
  const name = star?.[1]
    ? decodeURIComponent(star[1])
    : (plain?.[1] ?? `invoice-${id}.pdf`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
